import { addDays, weekday, type IsoDate } from '../dates';
import { isRecipeAllowed } from '../diet';
import type { Household } from '../household';
import type { PlanWarning } from '../plan';
import { isQuick, type Recipe } from '../recipe';
import type { Product } from '../types';

export interface DayRules {
  date: IsoDate;
  needsQuick: boolean;
  needsLunchbox: boolean;
}

/**
 * DE: Regeln eines Abends: schnell an Arbeitstagen und müden Tagen, Lunchbox vor Arbeitstagen.
 * EN: Rules for an evening: quick on workdays and low-energy days, lunchbox before workdays.
 */
export function dayRules(date: IsoDate, household: Household): DayRules {
  const isWorkday = household.workdays.includes(weekday(date));
  const nextIsWorkday = household.workdays.includes(weekday(addDays(date, 1)));
  return {
    date,
    needsQuick: isWorkday || household.lowEnergyDates.includes(date),
    needsLunchbox: nextIsWorkday,
  };
}

/**
 * DE: Erlaubte Abendessen, die die Regeln des Tages erfüllen (Reihenfolge bleibt erhalten).
 * EN: Allowed dinners that meet the day's rules (order is kept).
 */
export function dinnerCandidates(
  recipes: readonly Recipe[],
  household: Household,
  catalog: ReadonlyMap<string, Product>,
  rules: DayRules,
): Recipe[] {
  return recipes.filter(
    (recipe) =>
      recipe.meal === 'dinner' &&
      (!rules.needsQuick || isQuick(recipe, household.quick)) &&
      (!rules.needsLunchbox || recipe.lunchboxFriendly) &&
      isRecipeAllowed(recipe, household.members, catalog),
  );
}

/**
 * DE: Wie `dinnerCandidates`, lockert aber bei leerer Liste erst „schnell“, dann „Lunchbox“.
 * EN: Like `dinnerCandidates`, but relaxes "quick" and then "lunchbox" when the list is empty.
 */
export function relaxedCandidates(
  recipes: readonly Recipe[],
  household: Household,
  catalog: ReadonlyMap<string, Product>,
  rules: DayRules,
): { recipes: Recipe[]; warning?: PlanWarning } {
  const strict = dinnerCandidates(recipes, household, catalog, rules);
  if (strict.length > 0) return { recipes: strict };
  const withoutQuick = dinnerCandidates(recipes, household, catalog, {
    ...rules,
    needsQuick: false,
  });
  if (withoutQuick.length > 0) {
    return { recipes: withoutQuick, warning: { code: 'relaxed-quick', date: rules.date } };
  }
  const relaxed = { ...rules, needsQuick: false, needsLunchbox: false };
  const anyDinner = dinnerCandidates(recipes, household, catalog, relaxed);
  return anyDinner.length > 0
    ? { recipes: anyDinner, warning: { code: 'relaxed-lunchbox', date: rules.date } }
    : { recipes: [] };
}
